
import { showDynamicError } from "../../utilities/helpers";
import MasonryGrid from "../../utilities/masonry-grid";

/**
 * Asynchronously fetches a batch of zone cards for a specific location via AJAX.
 *
 * This function is designed to work with a server-side route that returns a partial HTML view.
 * In addition to the HTML, the server also embeds metadata into a hidden span with a data attribute.
 * This metadata includes total item count, items per page, and number of pages.
 * The `data-resp` attribute on the span is used to pass this information back to the client.
 * The server-side code generates this span within the partial blade view like this:
 * <span id="zone-resp" data-resp='@json(["totalCount" => $totalCount ?? null])'></span>
 * The client-side code then accesses this data using `const respData = $('#zone-resp').data('resp');`.
 *
 * The function relies on a pre-existing HTML element with a data-obj attribute that contains the
 * necessary `token` and `route` for the AJAX call. This element looks like this:
 * <div id="acct-zone-cards-target" class="d-none hide" data-obj='@json([
 * 'token' => csrf_token(),
 * 'route' => route('account.get.zones.route', $account->getId())
 * ])'></div>
 *
 * @param {number} start The pagination starting point (e.g., page number).
 * @param {number} limit The number of items to load per requests.
 * @returns {Promise<Object>} A promise that resolves with an object containing:
 * - newTiles: An array of DOM elements representing the new grid items.
 * - otherChildren: An array of DOM elements that are children but not grid items.
 * - totalCount: The total number of items available from the server.
 */
export const getOrderZoneCards = (start, limit) => {
    return new Promise((resolve, reject) => {
        const targetElement = document.getElementById('order-zone-cards-target');
        const dataObj = $(targetElement).data('obj');
        let route = dataObj.route;

        const requestData = {
            _token: dataObj.token,
            start: start,
            limit: limit,
        };

        const queryString = new URLSearchParams(requestData).toString();

        if (route.indexOf('?') !== -1) {
            route += `&${queryString}`;
        } else {
            route += `?${queryString}`;
        }

        $(targetElement).load(route, (responseText, status, xhr) => {
            if (status === "success") {
                const $cardsTempTarget = $(targetElement);
                const cardsArr = $cardsTempTarget.find('.masonry-grid-item').toArray();
                const otherChildren = $cardsTempTarget.children(':not(.masonry-grid-item)').toArray();
                const respData = $('#zone-resp').data('resp');

                $cardsTempTarget.empty();

                resolve({ 
                    newTiles: cardsArr,
                    otherChildren: otherChildren,
                    totalCount: respData.totalCount 
                });
            } else {
                console.error("AJAX Error:", status, xhr.statusText);
                reject(new Error(`AJAX Error: ${status}, ${xhr.statusText}`));
                showDynamicError('Failed to load zones. Please try again.');
            }
        });
    });
};

export const getAccountZoneCards = (start, limit, showInactive = false) => {
    return new Promise((resolve, reject) => {
        const targetElement = document.getElementById('acct-zone-cards-target');
        const dataObj = $(targetElement).data('obj');
        let route = dataObj.route;

        // is showinactive is toggles, add or update request data to have showInactive = true and update the gris resetting the page to the first page so that the pagination isn't messe dup
        const requestData = {
            _token: dataObj.token,
            start: start,
            limit: limit,
            showInactive: showInactive ? 1 : 0, 
        };

        const queryString = new URLSearchParams(requestData).toString();
        route += `?${queryString}`;

        $(targetElement).load(route, (responseText, status, xhr) => {
            if (status === "success") {
                const $cardsTempTarget = $(targetElement);
                const cardsArr = $cardsTempTarget.find('.masonry-grid-item').toArray();
                const otherChildren = $cardsTempTarget.children(':not(.masonry-grid-item)').toArray();
                const respData = $('#zone-resp').data('resp');

                $cardsTempTarget.empty();

                resolve({
                    newTiles: cardsArr,
                    otherChildren: otherChildren,
                    totalCount: respData.totalCount
                });
            } else {
                console.error("AJAX Error:", status, xhr.statusText);
                reject(new Error(`AJAX Error: ${status}, ${xhr.statusText}`));
                showDynamicError('Failed to load zones. Please try again.');
            }
        });
    });
};

document.addEventListener('DOMContentLoaded', async () => {
    // order page
    if (document.getElementById('order-zone-cards-target')) {
        const orderZones = await MasonryGrid.create(getOrderZoneCards);
    }

    // account details page
    if (document.getElementById('acct-zone-cards-target')) {
        const accountZones = await MasonryGrid.create(getAccountZoneCards);
    

        const $toggleInactiveZones = $('#toggleInactiveZones');
        // Prevents a browser caching issue where the checkbox state persists across reloads.
        $toggleInactiveZones.prop('checked', false);
        $toggleInactiveZones.on('change', () => {
            const showInactive = $toggleInactiveZones.is(":checked");
            if (showInactive) {
                $('#toggleInactiveZonesLabel').text("Hide Inactive");
            } else {
                $('#toggleInactiveZonesLabel').text("Show Inactive");
            }

            // Reload the grid, passing the new state
            accountZones.reload({ showInactive });
        });
    }
});